import { createAsyncThunk } from '@reduxjs/toolkit'
import { booleanSubtract, booleanUnite } from '~/lib/svg/booleanOps'
import { extractCurves, curvesToSegment } from '~/lib/svg/eraserUtils'
import { measureCharPositions, measureText } from '~/lib/svg/measureText'
import { buildFlipUpdate } from '~/lib/flipObject'
import { isLocked, originOf, positionFromOrigin } from '~/lib/objectBounds'
import { rawPathToString, resetPathOrigin, stringToRawPath, transformRawPath } from '~/lib/svg/paths'
import { expandTextToPath } from '~/lib/svg/textToPath'
import { objectProps } from '~/lib/presetSettings'
import { getObjectInitial } from '~/lib/params'
import { setObjectSelection } from '~/store/selection'
import { startBatch, endBatch } from '~/store/middleware/history'
import {
	addObjects,
	bringObjectToFront,
	deleteObjects,
	groupObjects,
	sendObjectToBack,
	unGroupObjects,
	updateObject,
	updateObjects,
} from './'

// Thunk to add object(s) and select them
export const addAndSelectObjects = createAsyncThunk('live/addAndSelect', async (payload, { getState, dispatch }) => {
	const beforeIds = new Set(Object.keys(getState().live.objects))
	dispatch(addObjects(payload))
	const afterObjects = getState().live.objects
	const newIds = Object.keys(afterObjects).filter(id => !beforeIds.has(id))
	if (newIds.length) dispatch(setObjectSelection(newIds))
})

// Thunk to group selected objects
export const groupSelectedObjectsThunk = createAsyncThunk(
	'live/groupSelectedObjects',
	async (_, { getState, dispatch }) => {
		const state = getState()
		const selectedObjectIds = state.selection.objectIds

		if (!selectedObjectIds || selectedObjectIds.length < 2) {
			console.warn('groupSelectedObjectsThunk: Not enough objects selected')
			return
		}

		// Dispatch the live slice action with selectedObjectIds as payload
		dispatch(groupObjects(selectedObjectIds))
	}
)

// Thunk to ungroup selected objects
export const unGroupSelectedObjectsThunk = createAsyncThunk(
	'live/unGroupSelectedObjects',
	async (_, { getState, dispatch }) => {
		const state = getState()
		const selectedObjectIds = state.selection.objectIds

		if (!selectedObjectIds || selectedObjectIds.length === 0) {
			console.warn('unGroupSelectedObjectsThunk: No objects selected')
			return
		}

		// Dispatch the live slice action with selectedObjectIds as payload
		dispatch(unGroupObjects(selectedObjectIds))
	}
)

// Thunk to bring selected objects to front
export const bringSelectedObjectsToFrontThunk = createAsyncThunk(
	'live/bringSelectedObjectsToFront',
	async (_, { getState, dispatch }) => {
		const state = getState()
		const selectedObjectIds = state.selection.objectIds

		if (!selectedObjectIds || selectedObjectIds.length === 0) {
			console.warn('bringSelectedObjectsToFrontThunk: No objects selected')
			return
		}

		// Dispatch the live slice action with selectedObjectIds as payload
		dispatch(bringObjectToFront(selectedObjectIds))
		// Selection does not change after bringing to front, so no need to update editor state
	}
)

// Thunk to send selected objects to back
export const sendSelectedObjectsToBackThunk = createAsyncThunk(
	'live/sendSelectedObjectsToBack',
	async (_, { getState, dispatch }) => {
		const state = getState()
		const selectedObjectIds = state.selection.objectIds

		if (!selectedObjectIds || selectedObjectIds.length === 0) {
			console.warn('sendSelectedObjectsToBackThunk: No objects selected')
			return
		}

		// Dispatch the live slice action with selectedObjectIds as payload
		dispatch(sendObjectToBack(selectedObjectIds))
		// Selection does not change after sending to back, so no need to update editor state
	}
)

// Thunk to lock selected objects
export const lockSelectedObjectsThunk = createAsyncThunk(
	'live/lockSelectedObjects',
	async (_, { getState, dispatch }) => {
		const state = getState()
		const selectedObjectIds = state.selection.objectIds

		if (!selectedObjectIds || selectedObjectIds.length === 0) {
			console.warn('lockSelectedObjectsThunk: No objects selected')
			return
		}

		dispatch(updateObjects(selectedObjectIds.map(id => ({ id, locked: true }))))
	}
)

// Thunk to unlock selected objects
export const unlockSelectedObjectsThunk = createAsyncThunk(
	'live/unlockSelectedObjects',
	async (_, { getState, dispatch }) => {
		const state = getState()
		const selectedObjectIds = state.selection.objectIds

		if (!selectedObjectIds || selectedObjectIds.length === 0) {
			console.warn('unlockSelectedObjectsThunk: No objects selected')
			return
		}

		// A map-linked shape stays locked until detached.
		const ids = selectedObjectIds.filter(id => !state.live.objects[id]?.map?.id)
		if (ids.length) dispatch(updateObjects(ids.map(id => ({ id, locked: false }))))
	}
)

// Thunk to break apart text objects into individual character text objects
export const breakApartTextThunk = createAsyncThunk('live/breakApartText', async (_, { getState, dispatch }) => {
	const state = getState()
	const selectedObjectIds = state.selection.objectIds
	if (!selectedObjectIds?.length) return

	const objects = state.live.objects
	const textObjs = selectedObjectIds
		.map(id => objects[id])
		.filter(o => o?.type === 'text' && o.text?.trim() && o.text.length > 1)

	if (!textObjs.length) return

	const idsToDelete = []
	const newObjects = []

	for (const obj of textObjs) {
		const displayText = obj.allCaps ? obj.text.toUpperCase() : obj.text
		const positions = measureCharPositions(
			displayText,
			obj.fontFamily || 'Arial',
			obj.fontSize || 24,
			obj.bold,
			obj.italic,
			obj.letterSpacing || 0,
			obj.lineHeight || 1.2,
			obj.textAlign || 'left'
		)

		const textScaleX = obj.textScaleX || 1

		for (const pos of positions) {
			if (!pos.char.trim()) continue // skip whitespace
			const charDims = measureText(
				pos.char,
				obj.fontFamily || 'Arial',
				obj.fontSize || 24,
				obj.bold,
				obj.italic,
				0,
				obj.lineHeight || 1.2
			)
			newObjects.push({
				type: 'text',
				text: obj.allCaps ? pos.char.toLowerCase() : pos.char,
				x: obj.x + pos.x * textScaleX,
				y: obj.y + pos.y,
				width: charDims.width * textScaleX,
				height: charDims.height,
				fontFamily: obj.fontFamily || 'Arial',
				fontSize: obj.fontSize || 24,
				bold: obj.bold || false,
				italic: obj.italic || false,
				allCaps: obj.allCaps || false,
				fill: obj.fill || '#ffffff',
				rotation: obj.rotation || 0,
				opacity: obj.opacity ?? 100,
				textScaleX,
				letterSpacing: 0,
				lineHeight: obj.lineHeight || 1.2,
				textAlign: 'left',
			})
		}
		idsToDelete.push(obj.id)
	}

	if (!newObjects.length) return

	// Count objects before adding to determine new IDs
	const beforeIds = new Set(Object.keys(getState().live.objects))
	dispatch(addObjects(newObjects))
	dispatch(deleteObjects(idsToDelete))
	const afterObjects = getState().live.objects
	const newIds = Object.keys(afterObjects).filter(id => !beforeIds.has(id))
	dispatch(setObjectSelection(newIds))
})

// Thunk to expand text objects to filled paths (glyph outlines)
export const expandTextThunk = createAsyncThunk('live/expandText', async (_, { getState, dispatch }) => {
	const state = getState()
	const selectedObjectIds = state.selection.objectIds
	if (!selectedObjectIds?.length) return

	const objects = state.live.objects
	const textObjs = selectedObjectIds.map(id => objects[id]).filter(o => o?.type === 'text' && o.text?.trim())

	if (!textObjs.length) return

	for (const obj of textObjs) {
		try {
			const expandObj = obj.allCaps ? { ...obj, text: obj.text.toUpperCase() } : obj
			const result = await expandTextToPath(expandObj)
			dispatch(
				updateObject({
					id: obj.id,
					type: 'path',
					data: result.data,
					x: result.x,
					y: result.y,
					width: result.width,
					height: result.height,
					fill: obj.fill || '#ffffff',
					stroke: '',
					strokeWidth: 0,
					text: '',
					fontFamily: '',
					fontSize: '',
					bold: '',
					italic: '',
					allCaps: '',
					letterSpacing: '',
					lineHeight: '',
					textAlign: '',
					textScaleX: '',
				})
			)
		} catch (err) {
			console.error(`Failed to expand text "${obj.text}" to path:`, err)
		}
	}
})

// ─── Boolean Ops helpers ────────────────────────────────────────────

/** Convert an object's path data to world-space curve8[][], baking in x/y/rotation/scale. */
export function objectToWorldCurves(obj) {
	const rp = stringToRawPath(obj.data)
	if (!rp || !rp.length) return null

	// The data frame sits at the object's data origin, not at its stored position (the pivot).
	const { x, y } = originOf(obj)
	const rot = ((obj.rotation ?? 0) * Math.PI) / 180
	const sx = obj.scaleX ?? 1,
		sy = obj.scaleY ?? 1
	const cos = Math.cos(rot),
		sin = Math.sin(rot)
	transformRawPath(rp, sx * cos, sx * sin, -sy * sin, sy * cos, x, y)
	return rp.map(seg => extractCurves(seg))
}

/** Convert curve8[][] contours to { data, x, y, width, height }. */
export function contoursToPathData(contours) {
	const segments = contours.map(c => curvesToSegment(c))
	const pathStr = rawPathToString(segments)
	const r = resetPathOrigin(pathStr)
	const rp = stringToRawPath(r.data)
	let maxX = 0,
		maxY = 0
	if (rp) {
		for (const seg of rp) {
			for (let i = 0; i < seg.length; i += 2) {
				if (seg[i] > maxX) maxX = seg[i]
				if (seg[i + 1] > maxY) maxY = seg[i + 1]
			}
		}
	}
	// World curves have no rotation, so the stored position is the unrotated pivot.
	const pos = positionFromOrigin({ type: 'path', data: r.data }, r)
	return { data: r.data, x: pos.x, y: pos.y, width: Math.round(maxX * 10) / 10, height: Math.round(maxY * 10) / 10 }
}

/** Build update patch for object A after boolean op. Handles brush→path expansion. */
function buildResultUpdate(objA, pathData) {
	const update = {
		id: objA.id,
		data: pathData.data,
		x: pathData.x,
		y: pathData.y,
		width: pathData.width,
		height: pathData.height,
		rotation: 0,
		scaleX: 1,
		scaleY: 1,
	}
	if (objA.type === 'brush') {
		update.type = 'path'
		update.centerline = ''
		update.rawPoints = ''
		update.variableWidth = false
		update.widthGrowth = 0
		update.strokeCap = ''
		update.smoothness = ''
		update.minWidth = ''
	}
	return update
}

// ─── Boolean Op Thunks ──────────────────────────────────────────────

/** Approximate total area of contours (curve8[][]) using anchor-point shoelace. */
function contoursArea(contours) {
	let total = 0
	for (const curves of contours) {
		let area = 0
		for (const c of curves) {
			area += c[0] * c[7] - c[6] * c[1]
		}
		total += Math.abs(area / 2)
	}
	return total
}

/** `payload.ids` names the operands; otherwise the selection does, first one kept. */
export const uniteSelectedObjectsThunk = createAsyncThunk(
	'live/uniteSelectedObjects',
	async (payload, { getState, dispatch }) => {
		const state = getState()
		const ids = Array.isArray(payload?.ids) ? payload.ids : state.selection.objectIds
		if (!ids || ids.length < 2) return

		const objects = state.live.objects
		const objA = objects[ids[0]]
		const bObjects = ids
			.slice(1)
			.map(id => objects[id])
			.filter(Boolean)
		if (!objA || !bObjects.length) return

		let allContours = objectToWorldCurves(objA)
		if (!allContours?.length) return

		const bIds = []
		for (const objB of bObjects) {
			const curvesB = objectToWorldCurves(objB)
			if (!curvesB?.length) continue

			const result = booleanUnite(allContours, curvesB)
			if (!result?.length) continue
			allContours = result
			bIds.push(objB.id)
		}

		if (!bIds.length) return

		dispatch(startBatch())
		try {
			dispatch(updateObject(buildResultUpdate(objA, contoursToPathData(allContours))))
			dispatch(deleteObjects(bIds))
			dispatch(setObjectSelection([objA.id]))
		} finally {
			dispatch(endBatch())
		}
	}
)

export const subtractSelectedObjectsThunk = createAsyncThunk(
	'live/subtractSelectedObjects',
	async (payload, { getState, dispatch }) => {
		const keepCutters = payload?.keepCutters ?? false
		const state = getState()
		const ids = Array.isArray(payload?.ids) ? payload.ids : state.selection.objectIds
		if (!ids || ids.length < 2) return

		const objects = state.live.objects
		const objA = objects[ids[0]]
		const bObjects = ids
			.slice(1)
			.map(id => objects[id])
			.filter(Boolean)
		if (!objA || !bObjects.length) return

		let allContours = objectToWorldCurves(objA)
		if (!allContours?.length) return

		const bIds = []
		let consumed = false
		for (const objB of bObjects) {
			const curvesB = objectToWorldCurves(objB)
			if (!curvesB?.length) continue

			const areaBefore = contoursArea(allContours)
			const result = booleanSubtract(allContours, curvesB)
			if (result.length === 0) {
				// A fully consumed
				consumed = true
				break
			}
			// Detect disjoint: area unchanged means no actual subtraction
			const areaAfter = contoursArea(result)
			if (Math.abs(areaBefore - areaAfter) < 1) {
				continue
			}
			allContours = result
			bIds.push(objB.id)
		}

		if (!consumed && !bIds.length) return

		dispatch(startBatch())
		try {
			if (consumed) {
				dispatch(deleteObjects([objA.id]))
				dispatch(setObjectSelection([]))
				return
			}
			dispatch(updateObject(buildResultUpdate(objA, contoursToPathData(allContours))))
			if (!keepCutters) dispatch(deleteObjects(bIds))
			dispatch(setObjectSelection([objA.id]))
		} finally {
			dispatch(endBatch())
		}
	}
)

// Thunk to flip selected objects
export const flipSelectedObjectsThunk = createAsyncThunk(
	'live/flipSelectedObjects',
	async (axis, { getState, dispatch }) => {
		const state = getState()
		const selectedIds = state.selection.objectIds
		if (!selectedIds?.length) return

		const objects = state.live.objects
		dispatch(startBatch())

		for (const id of selectedIds) {
			const obj = objects[id]
			if (!obj || isLocked(obj, objects)) continue
			const patch = buildFlipUpdate(obj, axis, objects)
			if (patch) dispatch(updateObject(patch))
		}

		dispatch(endBatch())
	}
)

const RESET_SKIP = new Set([
	'id',
	'type',
	'name',
	'parentId',
	'children',
	'source',
	'fill',
	'data',
	'centerline',
	'rawPoints',
	'eraserMask',
	'mask',
])

export const resetObjectsThunk = createAsyncThunk('live/resetObjects', async (_arg, { getState, dispatch }) => {
	const state = getState()
	const selectedIds = state.selection.objectIds
	if (!selectedIds?.length) return

	const objects = state.live.objects
	dispatch(startBatch())

	for (const id of selectedIds) {
		const obj = objects[id]
		if (!obj) continue
		const props = objectProps(obj.type)
		if (!props) continue

		const patch = { id }
		for (const [key, schema] of Object.entries(props)) {
			if (RESET_SKIP.has(key) || schema.readOnly || schema.const !== undefined) continue
			const initial = getObjectInitial(obj.type, key)
			if (initial === undefined) continue
			patch[key] = initial
		}

		dispatch(updateObject(patch))
	}

	dispatch(endBatch())
})
